
import React from "react";
import { Button } from "@/components/ui/button";
import { Loader2, CreditCard } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { PaymentMethodsList } from "./payment-methods";
import MpesaPaymentForm from "./MpesaPayment";
import { usePaymentProcess } from "./usePaymentProcess";
import { getPaymentMethods } from "./paymentMethods";
import { PaymentOptionsDialogProps } from "./types";

const PaymentOptionsDialog = ({
  open,
  onOpenChange,
  planName,
  planPrice,
  currency,
  formatPrice,
  planId,
  returnUrl
}: PaymentOptionsDialogProps) => {
  const {
    isProcessing,
    selectedMethod,
    mpesaStatus,
    statusCheckCount,
    userPhone,
    userCountry,
    handleSelectPaymentMethod,
    handleMpesaSubmit,
    handleContinueWithPendingPayment,
    handleResetSelection
  } = usePaymentProcess({
    planName,
    planPrice,
    currency,
    planId,
    returnUrl,
    onOpenChange
  });

  const paymentMethods = getPaymentMethods(userCountry);

  const renderContent = () => {
    if (!selectedMethod) {
      return (
        <PaymentMethodsList 
          paymentMethods={paymentMethods}
          onSelectPaymentMethod={handleSelectPaymentMethod}
          isProcessing={isProcessing}
          userCountry={userCountry}
        />
      );
    }

    if (selectedMethod === "mpesa") {
      return (
        <MpesaPaymentForm
          mpesaStatus={mpesaStatus}
          statusCheckCount={statusCheckCount}
          isProcessing={isProcessing}
          defaultPhoneNumber={userPhone}
          onSubmit={handleMpesaSubmit}
          onRetry={handleResetSelection}
          onContinue={handleContinueWithPendingPayment}
        />
      );
    }

    return (
      <div className="py-6 flex flex-col items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-center">Processing your payment...</p>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Choose Payment Method</DialogTitle>
          <DialogDescription>
            You're upgrading to the {planName} plan ({formatPrice(planPrice, currency)}/month)
          </DialogDescription>
        </DialogHeader>
        
        {renderContent()}
        
        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between sm:space-x-2">
          {!isProcessing && mpesaStatus !== "initiated" && mpesaStatus !== "completed" && (
            <Button 
              variant="outline" 
              onClick={selectedMethod ? handleResetSelection : () => onOpenChange(false)}
            >
              {selectedMethod ? "Back" : "Cancel"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentOptionsDialog;
